import React, { useMemo, useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/site/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  Gauge,
  FileText,
  Warning,
  CreditCard,
  UploadSimple,
  ListChecks,
  ChartBar,
  Users,
  SignOut,
  ArrowUpRight,
  ShieldCheck,
  Article,
  IdentificationCard,
  ClockCounterClockwise,
  UserCircle,
  List,
  X,
  Receipt,
} from "@phosphor-icons/react";

const STAFF_ROLES = ["admin", "cpa", "caller", "document_team"];

const items = [
  { to: "/dashboard", label: "Mission Control", icon: Gauge, exact: true },
  { to: "/dashboard/basic-info", label: "Basic Info", icon: UserCircle },
  { to: "/dashboard/returns", label: "Tax Returns", icon: FileText },
  { to: "/dashboard/notices", label: "IRS Notices", icon: Warning },
  { to: "/dashboard/payments", label: "Payments", icon: CreditCard, roles: STAFF_ROLES },
  { to: "/dashboard/invoices", label: "Invoices", icon: Receipt },
  { to: "/dashboard/documents", label: "Documents", icon: UploadSimple },
  { to: "/dashboard/workflow", label: "Workflow", icon: ListChecks, roles: STAFF_ROLES },
  { to: "/dashboard/analytics", label: "Analytics", icon: ChartBar },
  { to: "/dashboard/blog-cms", label: "Blog CMS", icon: Article, roles: ["admin"] },
  { to: "/dashboard/employees", label: "Employees", icon: IdentificationCard, roles: ["admin"] },
  { to: "/dashboard/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/dashboard/audit-log", label: "Audit Log", icon: ClockCounterClockwise, roles: ["admin"] },
  { to: "/dashboard/security", label: "Security", icon: ShieldCheck },
];

function NavList({ visibleItems, onNavigate }) {
  return (
    <nav className="flex-1 p-3 space-y-1 tf-scroll overflow-y-auto">
      {visibleItems.map(({ to, label, icon: Icon, exact }) => (
        <NavLink
          key={to}
          to={to}
          end={exact}
          onClick={onNavigate}
          data-testid={`sidebar-${label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium border ${
              isActive
                ? "bg-[#0A2540] text-white border-[#0A2540]"
                : "text-slate-700 border-transparent hover:bg-slate-100 hover:border-slate-200"
            }`
          }
        >
          <Icon size={16} weight="bold" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarFooter({ user, onBackToSite, onLogout }) {
  return (
    <div className="p-3 border-t tf-hairline space-y-2">
      <div className="rounded-md border tf-hairline bg-slate-50 p-3">
        <div className="text-xs text-slate-500 uppercase tracking-wider">Signed in as</div>
        <div className="mt-1 text-sm font-semibold text-[#0A2540] truncate" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        <div className="mt-2 inline-flex text-[10px] font-mono uppercase tracking-widest bg-[#D32F2F]/10 text-[#D32F2F] px-2 py-0.5 rounded">
          {user?.role}
        </div>
      </div>
      <Button variant="outline" className="w-full" onClick={onBackToSite} data-testid="sidebar-back-site">
        Back to site <ArrowUpRight size={14} className="ml-1" />
      </Button>
      <Button variant="ghost" className="w-full text-[#D32F2F] hover:text-[#D32F2F]" onClick={onLogout} data-testid="sidebar-logout">
        <SignOut size={14} className="mr-2" /> Log out
      </Button>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const visibleItems = useMemo(
    () => items.filter((i) => !i.roles || i.roles.includes(user?.role)),
    [user?.role]
  );

  // Close drawer whenever the route changes (so tapping a link dismisses it).
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Prevent body scroll when the drawer is open on mobile.
  useEffect(() => {
    if (!drawerOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, [drawerOpen]);

  const backToSite = () => navigate("/");
  const activeLabel = useMemo(() => {
    const match = visibleItems.find(
      (i) => (i.exact && location.pathname === i.to) || (!i.exact && location.pathname.startsWith(i.to)),
    );
    return match?.label || "Dashboard";
  }, [location.pathname, visibleItems]);

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="dashboard-layout">
      {/* DESKTOP sidebar (>=lg) */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r tf-hairline bg-white">
        <div className="p-5 border-b tf-hairline">
          <Logo />
        </div>
        <NavList visibleItems={visibleItems} />
        <SidebarFooter user={user} onBackToSite={backToSite} onLogout={logout} />
      </aside>

      {/* MOBILE drawer + scrim (<lg) */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
          data-testid="mobile-drawer-scrim"
          aria-hidden="true"
        />
      )}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col border-r tf-hairline bg-white shadow-2xl transition-transform duration-200 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-drawer"
        aria-hidden={!drawerOpen}
      >
        <div className="p-4 border-b tf-hairline flex items-center justify-between">
          <Logo />
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDrawerOpen(false)} aria-label="Close menu" data-testid="mobile-drawer-close">
            <X size={18} />
          </Button>
        </div>
        <NavList visibleItems={visibleItems} onNavigate={() => setDrawerOpen(false)} />
        <SidebarFooter user={user} onBackToSite={backToSite} onLogout={logout} />
      </aside>

      <main className="flex-1 min-w-0">
        {/* MOBILE top bar */}
        <div className="lg:hidden sticky top-0 z-40 tf-glass px-3 py-2.5 flex items-center gap-2 border-b tf-hairline">
          <Button
            size="sm"
            variant="ghost"
            className="h-9 w-9 p-0"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <List size={20} />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Taxfree Filer LLP</div>
            <div className="text-sm font-[Chivo] font-bold text-[#0A2540] truncate">{activeLabel}</div>
          </div>
          <Button size="sm" variant="outline" className="h-9" onClick={logout} data-testid="mobile-dash-logout">
            <SignOut size={14} className="mr-1" /> Logout
          </Button>
        </div>

        <div className="p-4 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
